import { FC, useMemo, useState, useEffect, useRef } from 'react';

import classNames from 'classnames';

import ModalWrap from '../../common/modal-wrap';
import { CloseCrossBtn } from '../../icons/close-cross-btn';
import { InlineLoader } from '../../common/inline-loader';

const makeUrl = (url: string) => {
  if (!url) return '';

  return url.startsWith('http') ? url : `${import.meta.env.VITE_REACT_APP_API_URL}${url}`;
};

interface IMacDeckGalleryModalProps {
  isOpen: boolean;
  deckName?: string;
  cards?: string[];
  onClose: () => void;
}

const DeckCardPreview: FC<{ src: string; index: number }> = ({ src, index }) => {
  const [loaded, setLoaded] = useState(false);
  const constructedSrc = makeUrl(src);
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>(0);

  useEffect(() => {
    if (ref.current) {
      const resize = () => {
        const width = ref.current?.offsetWidth || 0;
        setHeight((width * 4) / 3);
      };
      resize();
      window.addEventListener('resize', resize);
      return () => window.removeEventListener('resize', resize);
    }
  }, []);

  return (
    <div
      ref={ref}
      style={{ height }}
      className="relative w-full overflow-hidden rounded-[18px] bg-grey2 shadow-[0_6px_16px_rgba(0,0,0,0.15),0_12px_32px_rgba(0,0,0,0.1)]"
    >
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-[rgba(0,0,0,0.15)]">
          <InlineLoader variant="small" />
        </div>
      )}
      <img
        src={constructedSrc}
        loading="lazy"
        alt={`card-${index}`}
        className={classNames(
          'absolute inset-0 h-full w-full object-cover bg-white rounded-[18px] scale-[1.02] transition-opacity duration-300 ease-out',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
      />
    </div>
  );
};

export const MacDeckGalleryModal: FC<IMacDeckGalleryModalProps> = ({ isOpen, deckName, cards, onClose }) => {
  const normalizedCards = useMemo(() => {
    if (!cards) return [];

    return cards.filter((card): card is string => typeof card === 'string' && card.length > 0);
  }, [cards]);

  return (
    <ModalWrap isShow={isOpen}>
      <div className="relative flex max-h-[90vh] w-[min(900px,92vw)] flex-col rounded-[32px] bg-white p-6 shadow-shadowPopup">
        <div className="mb-4 flex items-center justify-between gap-4">
          <h2 className="font-comfortaa text-lg font-bold text-blackBase lg:text-2xl">
            {deckName ? `Колода «${deckName}»` : 'Колода'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-grey1 hover:bg-grey2 transition-colors"
            aria-label="Закрыть"
          >
            <CloseCrossBtn width={18} height={18} />
          </button>
        </div>
        <div className="text-sm text-greyBase mb-6">
          Карты отображаются лицевой стороной. Вы можете пролистать галерею, чтобы оценить содержание колоды.
        </div>
        <div className="grid flex-1 grid-cols-2 gap-4 overflow-y-auto overflow-x-hidden pr-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {normalizedCards.length > 0 ? (
            normalizedCards.map((card, idx) => <DeckCardPreview src={card} index={idx} key={`${card}-${idx}`} />)
          ) : (
            <div className="col-span-full flex h-full min-h-[200px] items-center justify-center rounded-[20px] bg-grey1 text-center text-sm text-greyBase">
              В этой колоде нет карт для отображения.
            </div>
          )}
        </div>
      </div>
    </ModalWrap>
  );
};
